import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../config';
import { handleFirebaseError } from '../error-handling';
import type { Course, CourseModule } from '@/components/education/types';

// Every course, for the admin/instructor editors. Beneficiary views pass the
// result through visibleCourses (lib/courses/courseVisibility) first.
export async function getCourses(): Promise<Course[]> {
  try {
    const coursesQuery = query(collection(db, 'courses'));
    const snapshot = await getDocs(coursesQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Course));
  } catch (error) {
    handleFirebaseError(error, 'Error fetching courses');
    throw error;
  }
}

export async function addCourse(course: Course) {
  try {
    // Ensure modules is an empty array for new courses
    const courseData = {
      ...course,
      modules: []
    };
    await setDoc(doc(db, 'courses', course.id), courseData);
  } catch (error) {
    handleFirebaseError(error, 'Error adding course');
    throw error;
  }
}

export async function updateCourse(courseId: string, courseData: Partial<Course>): Promise<void> {
  try {
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (!courseDoc.exists()) {
      throw new Error(`Course with ID ${courseId} does not exist`);
    }

    await updateDoc(courseRef, courseData);
  } catch (error) {
    handleFirebaseError(error, 'Error updating course');
    throw error;
  }
}

export async function deleteCourse(courseId: string): Promise<void> {
  try {
    const courseRef = doc(db, 'courses', courseId);
    await deleteDoc(courseRef);
  } catch (error) {
    handleFirebaseError(error, 'Error deleting course');
    throw error;
  }
}

export async function addModuleToCourse(courseId: string, module: CourseModule): Promise<void> {
  try {
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (!courseDoc.exists()) {
      throw new Error('Course not found');
    }

    const courseData = courseDoc.data() as Course;
    const updatedModules = [...courseData.modules, module];
    
    await updateDoc(courseRef, {
      modules: updatedModules,
      totalLessons: updatedModules.length
    });
  } catch (error) {
    handleFirebaseError(error, 'Error adding module to course');
    throw error;
  }
}

export async function updateModuleInCourse(
  courseId: string,
  moduleId: string,
  moduleData: Partial<CourseModule>
): Promise<void> {
  try {
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (!courseDoc.exists()) {
      throw new Error('Course not found');
    }

    const courseData = courseDoc.data() as Course;
    const updatedModules = courseData.modules.map(module => 
      module.id === moduleId ? { ...module, ...moduleData } : module
    );
    
    await updateDoc(courseRef, { modules: updatedModules });
  } catch (error) {
    handleFirebaseError(error, 'Error updating module in course');
    throw error;
  }
}

export async function deleteModuleFromCourse(courseId: string, moduleId: string): Promise<void> {
  try {
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (!courseDoc.exists()) {
      throw new Error('Course not found');
    }

    const courseData = courseDoc.data() as Course;
    const updatedModules = courseData.modules.filter(module => module.id !== moduleId);
    
    await updateDoc(courseRef, {
      modules: updatedModules,
      totalLessons: updatedModules.length
    });
  } catch (error) {
    handleFirebaseError(error, 'Error deleting module from course');
    throw error;
  }
} 